import { distance, localToWorld, obbCorners, type Vec2 } from '@indusketch/geometry';
import type {
  Fence,
  FenceKind,
  Gate,
  Parking,
  ParkingKind,
  ProjectDocument,
  Road,
  RoadKind,
} from './types.js';
import { modelTexts } from './texts.js';

export interface RoadPreset {
  readonly kind: RoadKind;
  readonly label: string;
  readonly width: number;
  readonly color: string;
}

/**
 * Typical planning widths: two-lane works road 6.5 m, truck access 7.5 m, fire lane 3.5 m
 * (DIN 14090 requires at least 3 m clear width), footpath 2 m. All editable per road.
 */
const road = (kind: RoadKind, width: number, color: string): RoadPreset => ({
  kind,
  get label() {
    return modelTexts().roads[kind];
  },
  width,
  color,
});

export const ROAD_CATALOG: readonly RoadPreset[] = [
  road('road', 6.5, '#6b7078'),
  road('truck-access', 7.5, '#5d6168'),
  road('fire-lane', 3.5, '#9a6a62'),
  road('footpath', 2, '#a9a49a'),
];

export function roadPreset(kind: RoadKind): RoadPreset {
  return ROAD_CATALOG.find((p) => p.kind === kind) ?? (ROAD_CATALOG[0] as RoadPreset);
}

export function polylineLength(points: readonly Vec2[]): number {
  let sum = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a && b) sum += distance(a, b);
  }
  return sum;
}

/** Paved area as centerline length × width; the small overlap/gap at bends is ignored. */
export const roadArea = (r: Pick<Road, 'points' | 'width'>): number =>
  polylineLength(r.points) * r.width;

export const fenceLength = (f: Pick<Fence, 'points'>): number => polylineLength(f.points);

export interface FenceKindInfo {
  readonly kind: FenceKind;
  readonly label: string;
  /** Width of one panel between posts. */
  readonly panelWidth: number;
  readonly height: number;
  readonly color: string;
}

const fenceKind = (
  kind: FenceKind,
  panelWidth: number,
  height: number,
  color: string,
): FenceKindInfo => ({
  kind,
  get label() {
    return modelTexts().fenceKinds[kind];
  },
  panelWidth,
  height,
  color,
});

/** Welded double-wire panels 2.50 m (standard 2.03 m high) and a 3.50 m construction fence. */
export const FENCE_KINDS: readonly FenceKindInfo[] = [
  fenceKind('mesh', 2.5, 2.03, '#4b545c'),
  fenceKind('mobile', 3.5, 2, '#b9bec3'),
];

export const fenceKindInfo = (kind: FenceKind): FenceKindInfo =>
  FENCE_KINDS.find((k) => k.kind === kind) ?? (FENCE_KINDS[0] as FenceKindInfo);

/**
 * Usual fence colours: RAL 7016 anthracite, RAL 6005 moss green, hot-dip galvanized. The
 * anthracite is a touch lighter and bluer than the RAL swatch, so thin wires do not look black
 * under the scene light.
 */
export const FENCE_COLORS = [
  { id: 'ral7016', color: '#4b545c' },
  { id: 'ral6005', color: '#2d4a37' },
  { id: 'galvanized', color: '#b9bec3' },
] as const;

export const GATE_WIDTH = { min: 4, max: 15, default: 6 } as const;
/** Clear width of a single-leaf pedestrian door in a fence. */
export const DOOR_WIDTH = { min: 0.8, max: 1.5, default: 1 } as const;

export const gateWidthRange = (kind: Gate['kind']) => (kind === 'door' ? DOOR_WIDTH : GATE_WIDTH);

/** Point and direction at distance `s` along a polyline (clamped to its ends). */
export function pointAlong(points: readonly Vec2[], s: number): { point: Vec2; dir: Vec2 } {
  let rest = Math.max(0, s);
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1] as Vec2;
    const b = points[i] as Vec2;
    const len = distance(a, b);
    if (len < 1e-9) continue;
    const dir = { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
    if (rest <= len || i === points.length - 1) {
      const t = Math.min(rest, len);
      return { point: { x: a.x + dir.x * t, y: a.y + dir.y * t }, dir };
    }
    rest -= len;
  }
  const p = points[0] ?? { x: 0, y: 0 };
  return { point: p, dir: { x: 1, y: 0 } };
}

/** Distance along the polyline of the point on it closest to `p`. */
export function distanceAlong(points: readonly Vec2[], p: Vec2): number {
  let best = { d: Infinity, s: 0 };
  let start = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1] as Vec2;
    const b = points[i] as Vec2;
    const len = distance(a, b);
    if (len > 1e-9) {
      const t = Math.max(
        0,
        Math.min(len, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / len),
      );
      const q = { x: a.x + ((b.x - a.x) * t) / len, y: a.y + ((b.y - a.y) * t) / len };
      const d = distance(p, q);
      if (d < best.d) best = { d, s: start + t };
    }
    start += len;
  }
  return best.s;
}

type GatePlacement = Pick<Gate, 'center' | 'rotation' | 'width'>;

/** Ends of the gate opening (local −x and +x side). */
export function gateEnds(g: GatePlacement): [Vec2, Vec2] {
  const d = { x: Math.cos(g.rotation) * (g.width / 2), y: Math.sin(g.rotation) * (g.width / 2) };
  return [
    { x: g.center.x - d.x, y: g.center.y - d.y },
    { x: g.center.x + d.x, y: g.center.y + d.y },
  ];
}

/** A gate counts as standing in a fence when both ends lie on its line (within this distance). */
export const GATE_ON_FENCE_TOLERANCE = 0.3;

function distanceToPolyline(points: readonly Vec2[], p: Vec2): number {
  return distance(p, pointAlong(points, distanceAlong(points, p)).point);
}

/** Stretch of the fence (distances along it) taken by a gate standing in it, or null. */
export function gateInterval(f: Pick<Fence, 'points'>, g: GatePlacement): [number, number] | null {
  const [a, b] = gateEnds(g);
  if (distanceToPolyline(f.points, a) > GATE_ON_FENCE_TOLERANCE) return null;
  if (distanceToPolyline(f.points, b) > GATE_ON_FENCE_TOLERANCE) return null;
  const s = [distanceAlong(f.points, a), distanceAlong(f.points, b)].sort((x, y) => x - y) as [
    number,
    number,
  ];
  // Both ends on the fence but far apart along it (e.g. across a corner): not in this fence.
  return s[1] - s[0] > g.width + GATE_ON_FENCE_TOLERANCE ? null : s;
}

/**
 * Straight fence pieces between the corners and the gates standing in the fence: the parts
 * that carry panels. Each piece knows where it starts along the fence, so panels line up.
 */
export function fenceRuns(
  f: Pick<Fence, 'points'>,
  gates: readonly GatePlacement[] = [],
): { a: Vec2; b: Vec2; start: number }[] {
  const gaps = gates
    .flatMap((g) => {
      const i = gateInterval(f, g);
      return i ? [i] : [];
    })
    .sort((x, y) => x[0] - y[0]);
  const runs: { a: Vec2; b: Vec2; start: number }[] = [];
  let s0 = 0;
  for (let i = 1; i < f.points.length; i++) {
    const a = f.points[i - 1] as Vec2;
    const b = f.points[i] as Vec2;
    const len = distance(a, b);
    const s1 = s0 + len;
    let from = s0;
    for (const [g0, g1] of gaps) {
      if (g1 <= from || g0 >= s1) continue;
      if (g0 > from + 1e-6)
        runs.push({
          a: pointAlong(f.points, from).point,
          b: pointAlong(f.points, g0).point,
          start: from,
        });
      from = Math.max(from, g1);
    }
    if (s1 > from + 1e-6) runs.push({ a: pointAlong(f.points, from).point, b, start: from });
    s0 = s1;
  }
  return runs;
}

/** Turn `angle` by half turns so it is closest to `reference` (a gate keeps its opening side). */
function alignedAngle(angle: number, reference: number): number {
  let a = angle;
  while (a - reference > Math.PI / 2) a -= Math.PI;
  while (reference - a > Math.PI / 2) a += Math.PI;
  return a;
}

/**
 * Snaps a gate being moved onto the nearest fence line within `tolerance`: its middle lands on
 * the line and it turns along it. Without a fence nearby it stays where it is.
 */
export function snapGateToFences(
  doc: Pick<ProjectDocument, 'fences'>,
  center: Vec2,
  rotation: number,
  tolerance: number,
): { center: Vec2; rotation: number } {
  let best: { d: number; center: Vec2; rotation: number } | null = null;
  for (const f of Object.values(doc.fences)) {
    const at = pointAlong(f.points, distanceAlong(f.points, center));
    const d = distance(center, at.point);
    if (d <= tolerance && (!best || d < best.d))
      best = {
        d,
        center: at.point,
        rotation: alignedAngle(Math.atan2(at.dir.y, at.dir.x), rotation),
      };
  }
  return best ? { center: best.center, rotation: best.rotation } : { center, rotation };
}

/** Middle of the longest fence piece that is still free: where a new gate goes into a fence. */
export function freeFenceSpot(
  f: Pick<Fence, 'points'>,
  gates: readonly GatePlacement[],
): { center: Vec2; rotation: number; length: number } {
  let best = { center: f.points[0] ?? { x: 0, y: 0 }, rotation: 0, length: 0 };
  for (const r of fenceRuns(f, gates)) {
    const length = distance(r.a, r.b);
    if (length > best.length)
      best = {
        center: { x: (r.a.x + r.b.x) / 2, y: (r.a.y + r.b.y) / 2 },
        rotation: Math.atan2(r.b.y - r.a.y, r.b.x - r.a.x),
        length,
      };
  }
  return best;
}

export interface ParkingPreset {
  readonly kind: ParkingKind;
  readonly label: string;
  readonly stallWidth: number;
  readonly stallDepth: number;
  readonly aisleWidth: number;
  readonly rows: 1 | 2;
}

/**
 * Car stalls 2.5 × 5.0 m with a 6 m aisle for 90° parking (common German practice, EAR);
 * truck stalls 3.5 × 18 m with a 15 m maneuvering strip. Illustrative planning values.
 */
const parking = (preset: Omit<ParkingPreset, 'label'>): ParkingPreset => ({
  ...preset,
  get label() {
    return modelTexts().parkings[preset.kind];
  },
});

export const PARKING_CATALOG: readonly ParkingPreset[] = [
  parking({ kind: 'car', stallWidth: 2.5, stallDepth: 5, aisleWidth: 6, rows: 2 }),
  parking({ kind: 'truck', stallWidth: 3.5, stallDepth: 18, aisleWidth: 15, rows: 1 }),
];

export function parkingPreset(kind: ParkingKind): ParkingPreset {
  return PARKING_CATALOG.find((p) => p.kind === kind) ?? (PARKING_CATALOG[0] as ParkingPreset);
}

type ParkingLayout = Pick<
  Parking,
  'rows' | 'stallsPerRow' | 'stallWidth' | 'stallDepth' | 'aisleWidth'
>;

export const parkingSize = (p: ParkingLayout): { width: number; depth: number } => ({
  width: p.stallsPerRow * p.stallWidth,
  depth: p.rows * p.stallDepth + p.aisleWidth,
});

export const parkingStallCount = (p: Pick<Parking, 'rows' | 'stallsPerRow'>): number =>
  p.rows * p.stallsPerRow;

export function parkingCorners(p: Parking): Vec2[] {
  const { width, depth } = parkingSize(p);
  return obbCorners({ center: p.center, width, depth, rotation: p.rotation });
}

/**
 * Stall rectangles in world coordinates. Local frame: stalls side by side along x, the first
 * row at the south edge; a single row has its aisle north of it, a double row in between.
 */
export function parkingStalls(p: Parking): Vec2[][] {
  const { width, depth } = parkingSize(p);
  const placement = { position: p.center, rotation: p.rotation };
  const rowStarts = p.rows === 1 ? [-depth / 2] : [-depth / 2, depth / 2 - p.stallDepth];
  const stalls: Vec2[][] = [];
  for (const y0 of rowStarts) {
    for (let i = 0; i < p.stallsPerRow; i++) {
      const x0 = -width / 2 + i * p.stallWidth;
      stalls.push(
        [
          { x: x0, y: y0 },
          { x: x0 + p.stallWidth, y: y0 },
          { x: x0 + p.stallWidth, y: y0 + p.stallDepth },
          { x: x0, y: y0 + p.stallDepth },
        ].map((q) => localToWorld(q, placement)),
      );
    }
  }
  return stalls;
}

/** Stalls that fit along a given length, e.g. for a parking area drawn as a rectangle. */
export const stallsForLength = (length: number, stallWidth: number): number =>
  Math.max(1, Math.floor(length / stallWidth + 1e-6));

export interface OutdoorSummary {
  readonly roadArea: number;
  readonly roadLength: number;
  readonly roadAreaByKind: Readonly<Record<RoadKind, number>>;
  readonly parkingArea: number;
  readonly carStalls: number;
  readonly truckStalls: number;
  readonly fenceLength: number;
}

export function outdoorSummary(doc: ProjectDocument): OutdoorSummary {
  const byKind: Record<RoadKind, number> = {
    road: 0,
    'truck-access': 0,
    'fire-lane': 0,
    footpath: 0,
  };
  let length = 0;
  for (const r of Object.values(doc.roads)) {
    byKind[r.kind] += roadArea(r);
    length += polylineLength(r.points);
  }
  let parkingArea = 0;
  let car = 0;
  let truck = 0;
  for (const p of Object.values(doc.parkings)) {
    const s = parkingSize(p);
    parkingArea += s.width * s.depth;
    if (p.kind === 'car') car += parkingStallCount(p);
    else truck += parkingStallCount(p);
  }
  return {
    roadArea: Object.values(byKind).reduce((a, b) => a + b, 0),
    roadLength: length,
    roadAreaByKind: byKind,
    parkingArea,
    carStalls: car,
    truckStalls: truck,
    fenceLength: Object.values(doc.fences).reduce((s, f) => s + fenceLength(f), 0),
  };
}
